'use client';

import { motion } from 'framer-motion';
import { Droplet, Wind, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ForecastDay } from '../../types/weatherApi';
import AnimatedIcon from './AnimatedIcon';

interface HourlyForecastModalProps {
  dayForecast: ForecastDay;
  onClose: () => void;
}

// Быстрые парсеры ISO (open-meteo обычно "YYYY-MM-DDTHH:mm")
function hhmmFromIso(iso: string) {
  const m = iso.match(/T(\d{2}:\d{2})/);
  if (m?.[1]) return m[1];
  try {
    return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}
function hourFromIso(iso: string) {
  const m = iso.match(/T(\d{2}):/);
  if (m?.[1]) return Number(m[1]);
  try {
    return new Date(iso).getHours();
  } catch {
    return -1;
  }
}

export default function HourlyForecastModal({ dayForecast, onClose }: HourlyForecastModalProps) {
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // В этой архитектуре данные догружаются снаружи (ForecastTabs),
  // поэтому loading определяется по наличию hour[]
  const loading = !dayForecast?.hour?.length;
  const [error] = useState<string | null>(null); // оставил для твоего блока, сейчас всегда null

  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const isToday = dayForecast.date === todayStr;
  const currentHour = new Date().getHours();

  const modalDateTitle = useMemo(() => {
    try {
      return new Date(dayForecast.date).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        weekday: 'long',
      });
    } catch {
      return dayForecast.date;
    }
  }, [dayForecast.date]);

  // Скролл к текущему часу (без массива refs)
  useEffect(() => {
    if (!isToday) return;
    if (loading) return;

    const t = window.setTimeout(() => {
      const el = listRef.current?.querySelector<HTMLElement>(`[data-hour="${currentHour}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);

    return () => window.clearTimeout(t);
  }, [isToday, currentHour, loading, dayForecast.date]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Почасовой прогноз"
    >
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        transition={{ type: 'spring', damping: 20, stiffness: 200 }}
        className="bg-slate-800/80 border border-sky-700 rounded-2xl shadow-2xl w-full max-w-3xl text-white overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-sky-800 flex justify-between items-center">
          <h2 className="text-xl font-bold capitalize text-main">{modalDateTitle}</h2>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            className="p-2 rounded-full cursor-pointer hover:bg-sky-700 transition-colors"
            aria-label="Закрыть"
            type="button"
          >
            <X size={24} />
          </button>
        </div>

        <div ref={listRef} className="max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="p-6">
              <div
                className="bg-slate-800/50 rounded-xl h-[320px] animate-pulse"
                aria-busy="true"
              />
            </div>
          )}

          {!loading && error && <div className="p-6 text-red-300">{error}</div>}

          {!loading &&
            !error &&
            dayForecast?.hour?.map((hour) => {
              const hhmm = hhmmFromIso(hour.time);
              const hh = hourFromIso(hour.time);
              const isCurrentHour = isToday && hh === currentHour;

              const windSpeedMs = Math.round(hour.wind_kph / 3.6);
              const windGustMs = Math.round(hour.wind_gust_kph / 3.6);

              // 👇 если в типах icon может быть undefined — даём fallback (иначе TS ругается)
              const iconPath = hour.condition?.icon ?? '/og/og-image.webp'; // лучше заменить на реальный lottie "unknown"

              return (
                <div
                  key={hour.time}
                  data-hour={hh}
                  className={`
                  grid grid-cols-3 md:grid-cols-[1fr_1fr_2fr_2fr_2fr] items-center gap-4 p-4
                  border-b border-slate-700/50 transition-all duration-200
                  ${isCurrentHour ? 'bg-sky-600/30 text-white' : 'even:bg-slate-900/20'}
                `}
                >
                  <div className="flex flex-col md:flex-row items-center gap-x-4">
                    <span className="font-bold text-lg">{hhmm}</span>
                    <span className="md:hidden text-xl font-light">
                      {Math.round(hour.temp_c)}°C
                    </span>
                  </div>

                  <div className="hidden md:flex items-center gap-4 justify-start">
                    <div className="w-14 h-14 flex-shrink-0">
                      {/* hour.condition.icon уже подготовлен в forecast.service */}
                      <AnimatedIcon path={iconPath} />
                    </div>
                    <div className="text-2xl font-light">{Math.round(hour.temp_c)}°C</div>
                  </div>

                  <div className="text-sm text-sky-200 text-center md:text-left capitalize">
                    {hour.condition.text}
                  </div>

                  <div className="flex items-center gap-4 justify-center text-sky-300">
                    <div className="flex items-center gap-2" title="Ветер">
                      <Wind size={20} />
                      <span className="w-16 text-left">{windSpeedMs} м/с</span>
                    </div>
                    <div className="hidden md:flex items-center gap-2" title="Порывы ветра">
                      <Wind size={24} />
                      <span className="w-16 text-left">{windGustMs} м/с</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 justify-center text-sky-300">
                    <div className="flex items-center gap-2" title="Влажность">
                      <Droplet size={20} />
                      <span className="w-12 text-left">{hour.humidity}%</span>
                    </div>
                    <div title="Осадки" className="hidden md:block w-12 text-left">
                      {hour.precipitation > 0 ? `${hour.precipitation} мм` : ''}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </motion.div>
    </motion.div>
  );
}
